import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE = "EKSELENS · ERIO";
const BASE = "https://app.ekselens.com";

// Locales currently translated. Used to emit `hreflang` alternates so
// Google indexes `/fr/login` separately from `/login` and surfaces the
// right one on `google.fr` vs `google.com`. Add an entry here when a
// new locale ships and every page that opts in via `localized` will
// pick it up automatically.
const ALTERNATE_LOCALES = ["fr", "de", "es"];

function _stripLocale(pathname) {
  // Drop the optional leading locale segment (`/fr/foo` → `/foo`).
  const m = pathname.match(/^\/(en|fr|de|es)(\/|$)/);
  return m ? pathname.slice(m[0].length - 1) || "/" : pathname || "/";
}

/**
 * Per-page SEO/meta. Sets the document title, description and canonical URL,
 * and keeps the Open Graph / Twitter tags in sync for shareable pages.
 *
 * Pages that have been translated should pass `localized` (defaults to
 * false). When true, Seo emits:
 *   - the correct canonical for the CURRENT locale (so /fr/login
 *     canonicalises to itself, not /login),
 *   - one `hreflang` alternate link per supported locale,
 *   - an `x-default` alternate pointing at the EN version (Google's
 *     fallback for locales we don't ship yet).
 */
export const Seo = ({ title, description, path = "", localized = false }) => {
  const fullTitle = title ? `${title} · ${SITE}` : `${SITE} — Enterprise Risk Intelligence`;

  // Detect current locale prefix from the URL (NOT i18n.language — we want
  // the resolved-route truth, not the user's preferred-but-not-active locale).
  const location = useLocation();
  const currentLocale = (() => {
    const m = (location?.pathname || "").match(/^\/(fr|de|es)(\/|$)/);
    return m ? m[1] : "en";
  })();

  // Treat `path` as the canonical EN-form path (no locale prefix). If the
  // caller accidentally passes `/fr/foo`, strip it.
  const enPath = _stripLocale(path || location?.pathname || "/");
  const canonicalPath = currentLocale === "en" ? enPath : `/${currentLocale}${enPath === "/" ? "" : enPath}`;
  const canonicalUrl = `${BASE}${canonicalPath}`;
  const OG_LOCALES = { fr: "fr_FR", de: "de_DE", es: "es_ES" };
  const ogLocale = OG_LOCALES[currentLocale] || "en_GB";

  return (
    <Helmet>
      <html lang={currentLocale} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={ogLocale} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {localized && (
        <link rel="alternate" hrefLang="en" href={`${BASE}${enPath}`} />
      )}
      {localized && ALTERNATE_LOCALES.map((loc) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={loc}
          href={`${BASE}/${loc}${enPath === "/" ? "" : enPath}`}
        />
      ))}
      {localized && (
        <link rel="alternate" hrefLang="x-default" href={`${BASE}${enPath}`} />
      )}
    </Helmet>
  );
};
